// Bind to one concrete socket. Cleanup must never queue an unsubscribe onto a
// replacement socket after an account change or reconnect.
export const createTeamPresenceConnection = ({ socket, refresh, reconnect, document, window }) => {
  let stopped = false;
  let subscribed = false;
  let generation = 0;
  let pending = null;
  let lastRefresh = 0;
  const current = (version) => !stopped && version === generation && socket.connected;
  const send = (event, payload) => new Promise((resolve, reject) => {
    socket.timeout(8000).emit(event, payload, (error, response) => {
      if (error) { reject(error); return; }
      if (!response?.ok) { reject(new Error(response?.error || 'Team subscription failed.')); return; }
      resolve(response);
    });
  });
  const refreshDirectory = () => {
    if (document.visibilityState !== 'visible') return;
    lastRefresh = Date.now();
    Promise.resolve(refresh()).catch(() => undefined);
  };
  const synchronize = () => {
    if (stopped || pending) return;
    const version = generation;
    const work = (async () => {
      if (!socket.connected) {
        if (document.visibilityState === 'visible') await reconnect();
        return;
      }
      const first = !subscribed;
      if (first) {
        await send('team:subscribe', {});
        if (!current(version)) return;
        subscribed = true;
      }
      await send('team:heartbeat', {
        status: document.visibilityState === 'visible' ? 'online' : 'away'
      });
      if (!current(version)) return;
      if (first || Date.now() - lastRefresh >= 60000) refreshDirectory();
    })().catch(() => {
      if (current(version)) subscribed = false;
    }).finally(() => {
      if (pending === work) pending = null;
    });
    pending = work;
  };
  const onDisconnect = () => {
    generation += 1;
    subscribed = false;
    pending = null;
  };
  const onConnect = () => {
    onDisconnect();
    synchronize();
  };
  const onVisible = () => {
    synchronize();
    // Recover events missed while the browser was suspended, even if its
    // transport still says connected or the heartbeat fails.
    refreshDirectory();
  };
  socket.on('connect', onConnect);
  socket.on('disconnect', onDisconnect);
  document.addEventListener('visibilitychange', onVisible);
  const timer = window.setInterval(() => {
    synchronize();
    if (Date.now() - lastRefresh >= 60000) refreshDirectory();
  }, 25000);
  synchronize();
  refreshDirectory();
  return () => {
    stopped = true;
    generation += 1;
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    socket.off('connect', onConnect);
    socket.off('disconnect', onDisconnect);
    if (socket.connected) socket.emit('team:unsubscribe', {});
  };
};
